import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "スマホで撮った商品写真の影を消したい 白背景加工の最速手順",
	description:
		"スマホ撮影の商品写真に影が入る原因と、アプリ不要でブラウザだけで影を消して白背景に仕上げる最速手順を解説。",
	publishDate: "2026-03-16",
	slug: "smartphone-shadow-removal-guide",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function SmartphoneShadowRemovalGuide() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							スマホで商品写真を撮ったら影が入ってしまった——これはECセラーなら誰でも経験する問題です。
							アプリも機材も不要で、ブラウザだけで影を消して白背景に仕上げる最速手順を紹介します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							なぜスマホ撮影で影が入るのか
						</h2>
						<p className="text-gray-700 mb-4">
							スタジオ撮影では複数の照明を使って影を打ち消しますが、スマホ撮影では光源が一方向になりがちです。
							主な原因は次の3つです：
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
							<li>
								<span className="font-semibold">窓からの自然光：</span>
								光が片側から当たり、反対側に影ができる
							</li>
							<li>
								<span className="font-semibold">
									室内照明（シーリングライト）：
								</span>
								真上から光が当たり、商品の下や周囲に影ができる
							</li>
							<li>
								<span className="font-semibold">
									スマホのカメラフラッシュ：
								</span>
								強い点光源のため、商品の真後ろに強い影ができる
							</li>
						</ul>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							撮影時に影を減らす方法（根本解決）
						</h2>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
							<li>
								窓の前に白い紙やレフ板を置いて光を反射させる（影を減らす）
							</li>
							<li>曇りの日の窓際で撮影する（拡散光で影が柔らかくなる）</li>
							<li>LEDライトを2方向から当てる（100均の卓上ライトでも有効）</li>
							<li>商品撮影ボックスを使う（3,000〜5,000円程度で入手可能）</li>
						</ul>
						<p className="text-gray-700">
							ただし、すでに撮影済みの写真に影が入っている場合は後処理で対応します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							後処理で影を消す最速手順
						</h2>

						<h3 className="text-lg font-medium mt-6 mb-3">
							方法1: しろぬきやで一括処理（最速・推奨）
						</h3>
						<p className="text-gray-700 mb-3">
							影ごと背景を除去して白背景に仕上げるのが最速の方法です。
						</p>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>しろぬきやを開く（スマホのブラウザからでも操作可能）</li>
							<li>撮影した商品写真をアップロード</li>
							<li>AIが商品だけを切り抜き、影を含む背景を自動除去</li>
							<li>白背景＋Amazon/楽天/メルカリ対応サイズで自動出力</li>
							<li>そのままダウンロード → EC各モールにアップロード</li>
						</ol>
						<p className="text-gray-600 text-sm mb-4">
							※
							影が商品の輪郭と重なっている場合は精度が下がることがあります。そのような場合は方法2を試してください。
						</p>

						<h3 className="text-lg font-medium mt-6 mb-3">
							方法2: スマホアプリで影だけ消す
						</h3>
						<p className="text-gray-700 mb-2">
							「影だけを消したい（背景は残したい）」場合はスタンプ修復ツールが有効です：
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
							<li>
								<span className="font-semibold">Snapseed（無料）：</span>
								「修復」ツールで影の部分をなぞる → 背景色で補完
							</li>
							<li>
								<span className="font-semibold">TouchRetouch（有料）：</span>
								影のブラシ選択 → ワンタップ除去
							</li>
							<li>
								<span className="font-semibold">
									Adobe Lightroom Mobile（無料）：
								</span>
								「ゴミ除去」ツールで影をタップ
							</li>
						</ul>

						<h3 className="text-lg font-medium mt-6 mb-3">
							方法3: PC + GIMPで精密処理
						</h3>
						<ol className="list-decimal list-inside space-y-2 text-gray-700 mb-4">
							<li>GIMPで写真を開く</li>
							<li>「ファジー選択」ツールで影の部分を選択</li>
							<li>「色相・彩度」で選択範囲の明度を上げる（影を薄くする）</li>
							<li>「色調曲線」でさらに調整</li>
							<li>背景を白で塗りつぶしてJPEG保存</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							AmazonなどECモール向けに仕上げるポイント
						</h2>
						<ul className="list-disc list-inside space-y-2 text-gray-700">
							<li>背景は「純白（#FFFFFF）」にする（グレーや生成りはNG）</li>
							<li>Amazonメイン画像：商品が画面の85%以上を占めること</li>
							<li>
								影は完全に除去するか、自然な「落ち影（ドロップシャドウ）」のみ残す
							</li>
							<li>出力形式：JPEG（白背景）またはPNG（白塗りつぶし済み）</li>
						</ul>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							スマホで撮影した画像に影が入っていても、しろぬきやが商品の輪郭を検出して影ごと背景を除去します。白背景へのリサイズまでを自動で行い、EC出品用画像を最速で仕上げられます。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
